import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import client, { resolveImageUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import HomeStatsRail from "@/components/HomeStatsRail";
import ShoppableFeedStrip from "@/components/ShoppableFeedStrip";
import { useSection } from "@/context/SiteContentContext";
import useSeo from "@/lib/useSeo";

const FALLBACK_HERO = {
  eyebrow: "Streetwear. Designed Different.",
  headline_l1: "Streetwear",
  headline_l2: "without",
  headline_l3: "limits.",
  body: "Independent Norwich design studio. Specialist DTF printing and engraving — limited drops, custom commissions, bulk production for clubs & businesses, and full white-label supply for trade. Concept to garment under one roof.",
  cta_primary_label: "Shop The Drops", cta_primary_href: "/shop",
  cta_secondary_label: "Start A Custom Project", cta_secondary_href: "/start-your-brand",
  cta_tertiary_label: "Trade & Bulk", cta_tertiary_href: "/trade",
  featured_image: "https://customer-assets.emergentagent.com/job_collection-catalog/artifacts/h58a0oec_All%20alien%20races%20anunnaki%20black%20tshirt%20001.png",
  featured_caption_label: "Featured drop",
  featured_caption_value: "Cosmic Assembly",
};
const FALLBACK_COLL = { eyebrow: "Index / Collections", title: "Browse by collection." };
const FALLBACK_FEAT = { eyebrow: "Now Available", title: "Featured pieces." };
const FALLBACK_CLUB = {
  eyebrow: "Subscription / Members Only", title: "The Moto Club.",
  body: "A monthly drop, delivered. Members choose which t-shirts they receive month by month — pick freely from our entire collection, including past drops and member-only releases, and lock in your selections through the planner. The monthly drop ships FREE, and members get reduced UK postage sitewide: £2.50 on extra t-shirt shop orders and £3.00 on accessories / mixed baskets.",
  cta_primary_label: "Join from £12.99 / mo", cta_primary_href: "/club",
  cta_secondary_label: "See the planner", cta_secondary_href: "/club/planner",
  image: "https://customer-assets.emergentagent.com/job_collection-catalog/artifacts/h58a0oec_All%20alien%20races%20anunnaki%20black%20tshirt%20001.png",
};

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [themes, setThemes] = useState([]);
  const [topReviews, setTopReviews] = useState([]);
  const [featuredStories, setFeaturedStories] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);
  const hero = useSection("home_hero", FALLBACK_HERO);
  const collSec = useSection("home_collections", FALLBACK_COLL);
  const featSec = useSection("home_featured", FALLBACK_FEAT);
  const clubSec = useSection("home_club", FALLBACK_CLUB);
  const reviewsSec = useSection("home_reviews", {
    eyebrow: "Real bikers, real reviews",
    title: "What our customers say.",
    subtitle: "Read all reviews",
    cta_label: "Read all reviews →",
    cta_href: "/reviews",
  });

  useEffect(() => {
    client.get("/products").then((r) => {
      const all = r.data || [];
      setProducts(all.slice(0, 6));
      // Shuffle for hero slideshow (max 8 random picks)
      const shuffled = [...all].sort(() => Math.random() - 0.5).slice(0, 8);
      setAllProducts(shuffled);
    }).catch(() => {});
    client.get("/themes").then((r) => setThemes(r.data)).catch(() => {});
    client.get("/reviews/recent?min_rating=4&limit=6").then((r) => setTopReviews(r.data || [])).catch(() => {});
    client.get("/stories/featured?limit=3").then((r) => setFeaturedStories(r.data || [])).catch(() => {});
  }, []);

  // Auto-advance the hero slideshow every 4 seconds while products are loaded
  useEffect(() => {
    if (allProducts.length < 2) return undefined;
    const timer = setInterval(() => setHeroIdx((i) => (i + 1) % allProducts.length), 4000);
    return () => clearInterval(timer);
  }, [allProducts.length]);

  const heroProduct = allProducts[heroIdx] || null;
  const heroImageSrc = heroProduct?.image_url || hero.featured_image;
  const heroCaptionValue = heroProduct?.title || hero.featured_caption_value;
  useSeo({
    title: "MotoWear | Independent Apparel & Bulk Printing Studio · Norwich UK",
    description: "Independent Norwich apparel studio. Limited-run graphic tees, bespoke t-shirt design, and professional bulk printing for businesses, events, and clubs across the UK. Crafted for the individual, scaled for the collective. Free UK shipping over £65.",
    keywords: "independent clothing brand Norwich, custom design studio UK, bespoke t-shirt printing, bulk t-shirt printing UK, wholesale custom apparel, branded merchandise Norwich, premium graphic tees, limited edition streetwear, custom workwear Norwich, apparel branding UK, monthly t-shirt club",
    image: resolveImageUrl(heroImageSrc),
    path: "/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        url: "https://motowear.co.uk/",
        name: "MotoWear",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://motowear.co.uk/shop?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
    ],
  });

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="border-b border-black/10" data-testid="home-hero">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-0">
          <div className="lg:col-span-7 px-6 md:px-12 pt-12 pb-16 lg:pt-24 lg:pb-24 flex flex-col">
            <div className="label-eyebrow moto-accent">{hero.eyebrow}</div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[7.5rem] leading-[0.9] tracking-tighter text-balance mt-10 lg:mt-12">
              {hero.headline_l1}<br />
              {hero.headline_l2}<br />
              <span className="italic">{(hero.headline_l3 || "").charAt(0)}</span>{(hero.headline_l3 || "").slice(1)}<span className="moto-accent">.</span>
            </h1>
            <p className="mt-10 max-w-md text-base md:text-lg text-black/70 leading-relaxed">
              {hero.body}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to={hero.cta_primary_href || "/shop"} className="bg-moto-gradient text-white px-8 py-4 label-eyebrow border border-[#FF3B30] hover:bg-black hover:[background-image:none] hover:border-black transition-colors shadow-[0_8px_24px_rgba(255,59,48,0.35)]" data-testid="hero-shop-cta">
                {hero.cta_primary_label}
              </Link>
              <Link to={hero.cta_secondary_href || "/collections"} className="px-8 py-4 label-eyebrow border border-black hover:bg-black hover:text-white transition-colors" data-testid="hero-collections-cta">
                {hero.cta_secondary_label}
              </Link>
              <Link to={hero.cta_tertiary_href || "/custom"} className="px-8 py-4 label-eyebrow border border-black hover:bg-black hover:text-white transition-colors" data-testid="hero-custom-cta">
                {hero.cta_tertiary_label}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative border-l border-black/10 bg-moto-gradient-soft flex flex-col">
            <div className="flex-1 relative flex items-center justify-center p-6 md:p-10 min-h-[420px] lg:min-h-0 overflow-hidden" data-testid="hero-slideshow">
              {(allProducts.length ? allProducts : [{ id: "fallback", image_url: hero.featured_image, title: heroCaptionValue }]).map((p, i) => (
                <Link
                  key={p.id || i}
                  to={p.id && p.id !== "fallback" ? `/product/${p.id}` : (hero.cta_primary_href || "/shop")}
                  className={`absolute inset-0 flex items-center justify-center p-6 md:p-10 transition-opacity duration-700 ${i === heroIdx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                  aria-hidden={i !== heroIdx}
                  data-testid={`hero-slide-${i}`}
                >
                  <img
                    src={resolveImageUrl(p.image_url)}
                    alt={p.title || "Featured drop"}
                    className="max-w-full max-h-full w-auto h-auto object-contain"
                    loading={i === 0 ? "eager" : "lazy"}
                  />
                </Link>
              ))}
              {allProducts.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="hero-slideshow-dots">
                  {allProducts.map((p, i) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setHeroIdx(i)}
                      aria-label={`Show ${p.title}`}
                      className={`h-1.5 transition-all ${i === heroIdx ? "w-6 bg-[#FF3B30]" : "w-1.5 bg-black/30 hover:bg-black/60"}`}
                      data-testid={`hero-slideshow-dot-${i}`}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="border-t border-black/10 px-6 md:px-10 py-4 flex items-center justify-between bg-white">
              <span className="label-eyebrow text-black/60">{hero.featured_caption_label}</span>
              <span className="label-eyebrow moto-accent" data-testid="hero-caption-value">{heroCaptionValue}</span>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do — strategic 4-block: positions MotoWear as brand+studio+manufacturing partner, not just a clothing shop */}
      <section className="border-b border-black/10 bg-white" data-testid="home-what-we-do">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-14">
            <div className="lg:col-span-7">
              <div className="label-eyebrow moto-accent">What we do</div>
              <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mt-4">
                Four ways<br />we make clothing<span className="moto-accent">.</span>
              </h2>
            </div>
            <p className="lg:col-span-5 text-base text-black/65 leading-relaxed max-w-md">
              From a single bespoke tee to a 5,000-piece private-label run — we own every stage in-house, so timelines stay tight and quality stays consistent.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10">
            {[
              { eyebrow: "01 · Limited Drops", title: "Drops worth chasing.", body: "Exclusive small-batch DTF-printed collections from our own designers. Never restocked. Members of the Moto Club get them first.", cta: "Shop drops", href: "/shop", testid: "what-we-do-drops" },
              { eyebrow: "02 · Custom Design", title: "One-off, made for you.", body: "Personal tees, gifts, band merch, art pieces. Send a brief, sketch, or just a vibe — our studio drafts, samples and DTF prints it.", cta: "Start a custom project", href: "/custom", testid: "what-we-do-custom" },
              { eyebrow: "03 · Bulk Orders", title: "Teams. Schools. Crews.", body: "From 10 hoodies for a stag-do to 500 tees for a festival. DTF printing on every order, engraving upgrades for hard goods, tiered pricing by volume.", cta: "Get a bulk quote", href: "/bulk-orders", testid: "what-we-do-bulk" },
              { eyebrow: "04 · Trade Supply", title: "Private label & wholesale.", body: "Launch your own brand on our DTF supply chain. White-label production, sampling, packaging, fulfilment. Trade pricing applies.", cta: "Apply for trade", href: "/trade", testid: "what-we-do-trade" },
            ].map((b) => (
              <Link to={b.href} className="bg-white p-8 lg:p-10 flex flex-col group hover:bg-[#FAFAFA] transition-colors min-h-[280px]" data-testid={b.testid} key={b.testid}>
                <div className="label-eyebrow text-black/40">{b.eyebrow}</div>
                <h3 className="font-display text-2xl md:text-3xl tracking-tight mt-4 leading-tight">{b.title}</h3>
                <p className="mt-3 text-sm text-black/65 leading-relaxed flex-1">{b.body}</p>
                <div className="mt-6 label-eyebrow text-xs flex items-center gap-2 group-hover:gap-3 transition-all">
                  {b.cta} <ArrowRight size={14} strokeWidth={1.5} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How We Print — DTF + engraving only. DTF is our exclusive print method. */}
      <section className="border-b border-black/10 bg-[#FAFAFA]" data-testid="home-how-we-print">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-10">
            <div className="lg:col-span-7">
              <div className="label-eyebrow moto-accent">How we make it</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tighter mt-3 leading-[0.95]">
                DTF printing. Engraving. That's it<span className="moto-accent">.</span>
              </h2>
            </div>
            <p className="lg:col-span-5 text-sm text-black/65 leading-relaxed">
              Every garment leaves the studio with <strong>Direct-to-Film (DTF) print</strong> — vivid photo-grade colour, stretch-friendly hand-feel, washable. Hard-good upgrades (mugs, keyrings, water bottles) use precision laser engraving for a permanent finish.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10">
            {[
              { tag: "Every garment", title: "DTF — Direct to Film", body: "Our exclusive print method. Photo-grade colour reproduction. Stretches with the garment. Vivid on dark fabrics. No minimum order. Lasts 50+ washes.", footnote: "Tees · Hoodies · Sweats · Caps · Tote bags · Everything wearable" },
              { tag: "Hard goods", title: "Precision engraving", body: "Laser engraving for ceramic mugs, stainless water bottles, acrylic keyrings and phone cases. Permanent finish — won't fade, peel or wash off.", footnote: "Mugs · Bottles · Keyrings · Phone cases" },
            ].map((m) => (
              <div className="bg-white p-7 lg:p-8 flex flex-col" key={m.title} data-testid={`how-we-print-${m.title.toLowerCase().split(' ')[0]}`}>
                <div className="label-eyebrow text-[#FF3B30]">{m.tag}</div>
                <h3 className="font-display text-2xl tracking-tight mt-3">{m.title}</h3>
                <p className="mt-3 text-sm text-black/65 leading-relaxed flex-1">{m.body}</p>
                <div className="mt-5 pt-4 border-t border-black/10 label-eyebrow text-[10px] text-black/45">{m.footnote}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* From Idea to Garment — 5-step process, conversion-critical for custom/bulk/trade buyers */}
      <section className="border-b border-black/10 bg-black text-white" data-testid="home-idea-to-garment">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 lg:py-28">
          <div className="max-w-3xl mb-16">
            <div className="label-eyebrow moto-accent">Our process</div>
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-4 leading-[0.95]">
              From idea to garment<span className="moto-accent">.</span>
            </h2>
            <p className="mt-6 text-base text-white/65 leading-relaxed">
              Five steps. Roughly 7–14 days for custom; 3–4 weeks for bulk runs. You see proofs at every gate.
            </p>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10">
            {[
              { n: "01", label: "Concept", body: "Brief us with words, a sketch, or just a mood board. Free consultation." },
              { n: "02", label: "Design", body: "Our studio mocks up the artwork, scales it physically, picks Pantones." },
              { n: "03", label: "Sample", body: "One garment printed for sign-off before any production run begins." },
              { n: "04", label: "Production", body: "DTF printing in-house at our Norwich studio. Hard goods finish with precision engraving." },
              { n: "05", label: "Delivery", body: "Royal Mail Tracked or Parcelforce for bulk. Tracking emailed on dispatch." },
            ].map((s) => (
              <li className="bg-black p-8 lg:p-10 flex flex-col min-h-[220px]" key={s.n} data-testid={`process-step-${s.n}`}>
                <span className="font-display text-5xl md:text-6xl text-white/15 tracking-tighter leading-none">{s.n}</span>
                <h3 className="font-display text-xl md:text-2xl tracking-tight mt-6">{s.label}</h3>
                <p className="mt-3 text-sm text-white/55 leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/start-your-brand" className="bg-[#FF3B30] text-white px-8 py-4 label-eyebrow hover:bg-white hover:text-black transition-colors" data-testid="process-cta-start-brand">
              Start your brand →
            </Link>
            <Link to="/bulk-orders" className="px-8 py-4 label-eyebrow border border-white/40 hover:bg-white hover:text-black transition-colors" data-testid="process-cta-bulk">
              Get a bulk quote
            </Link>
          </div>
        </div>
      </section>

      {/* Club teaser */}
      <ShoppableFeedStrip />
      <HomeStatsRail />
      <section className="grid grid-cols-1 lg:grid-cols-2 border-b border-black/10 bg-moto-gradient-dark text-white" data-testid="home-club">
        <div className="aspect-[4/3] lg:aspect-auto relative border-r border-white/10">
          <img src={resolveImageUrl(clubSec.image)} alt="Moto Club" className="absolute inset-0 w-full h-full object-cover mix-blend-luminosity opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#7A0A1E]/40 to-transparent" />
        </div>
        <div className="p-12 md:p-24 flex flex-col justify-center">
          <div className="label-eyebrow text-white/60">{clubSec.eyebrow}</div>
          <h2 className="font-display text-4xl md:text-6xl mt-3 tracking-tighter">{clubSec.title}</h2>
          <p className="mt-8 text-base md:text-lg text-white/85 leading-relaxed max-w-lg">{clubSec.body}</p>

          {/* Iter41 — first-month welcome deal callout */}
          <div className="mt-8 inline-block bg-white/[0.08] border border-white/30 px-5 py-4 max-w-lg" data-testid="home-club-welcome-deal">
            <div className="label-eyebrow text-white/70 text-[10px]">First-month welcome rate</div>
            <div className="font-display text-2xl md:text-3xl mt-1 tracking-tight">
              <span className="text-white">£6.99</span>
              <span className="text-white/55 text-base ml-2">first month · 1 tee</span>
            </div>
            <div className="font-display text-2xl md:text-3xl mt-0.5 tracking-tight">
              <span className="text-white">£12.00</span>
              <span className="text-white/55 text-base ml-2">first month · 2 tees</span>
            </div>
            <p className="text-xs text-white/55 mt-2 leading-relaxed">
              Half-price your first delivery. Standard rate (£12.99 / £22) kicks in from month two.
              One welcome rate per customer for life — cancel any time before the next cycle.
            </p>
          </div>
          <div className="mt-4 inline-flex items-center gap-3 bg-white/[0.06] border border-white/25 px-5 py-3" data-testid="home-club-shipping-perk">
            <span className="label-eyebrow text-white/70 text-[10px]">Member postage</span>
            <span className="text-white text-sm leading-snug">
              <strong>£1.50</strong> on subscription t-shirts · <strong>£2.50</strong> on accessories
            </span>
          </div>

          <div className="mt-8 flex flex-wrap gap-4">
            <Link to={clubSec.cta_primary_href || "/club"} className="bg-white text-[#C8102E] px-8 py-4 label-eyebrow border border-white hover:bg-transparent hover:text-white transition-colors" data-testid="home-club-cta">
              {clubSec.cta_primary_label}
            </Link>
            {clubSec.cta_secondary_label && (
              <Link to={clubSec.cta_secondary_href || "/club/planner"} className="px-8 py-4 label-eyebrow border border-white/60 text-white hover:bg-white hover:text-[#C8102E] transition-colors" data-testid="home-club-planner-cta">
                {clubSec.cta_secondary_label}
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Collections */}
      <section className="border-b border-black/10 py-24 md:py-32 bg-moto-gradient-soft" data-testid="home-collections">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-16">
            <div>
              <div className="label-eyebrow moto-accent">{collSec.eyebrow}</div>
              <h2 className="font-display text-4xl md:text-6xl mt-3 tracking-tighter">{collSec.title}</h2>
            </div>
            <Link to="/collections" className="hidden md:flex label-eyebrow items-center gap-2 border-b border-[#C8102E] moto-accent pb-1">All collections <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#C8102E]/30">
            {themes.map((t, idx) => {
              // Up to 3 product thumbnails per collection for a quick visual peek.
              const thumbs = allProducts
                .filter((p) => p.theme_slug === t.slug || (p.extra_theme_slugs || []).includes(t.slug))
                .slice(0, 3)
                .map((p) => resolveImageUrl(p.image_url))
                .filter(Boolean);
              return (
                <Link
                  key={t.id}
                  to={`/collection/${t.slug}`}
                  className="bg-white p-6 md:p-8 hover:bg-moto-gradient hover:text-white transition-all duration-300 group flex flex-col"
                  data-testid={`collection-card-${t.slug}`}
                >
                  <div className="flex items-baseline justify-between">
                    <div className="label-eyebrow text-black/40 group-hover:text-white/70 transition-colors">No. {String(idx + 1).padStart(2, "0")}</div>
                    <div className="label-eyebrow moto-accent group-hover:text-white transition-colors">Collection</div>
                  </div>
                  <div className="font-display text-3xl md:text-4xl tracking-tighter mt-5">
                    {t.name}<span className="moto-accent group-hover:text-white transition-colors">.</span>
                  </div>
                  {t.description && <div className="mt-3 text-sm text-black/55 group-hover:text-white/80 leading-relaxed line-clamp-2 transition-colors">{t.description}</div>}
                  {thumbs.length > 0 && (
                    <div className="mt-6 grid grid-cols-3 gap-1.5" data-testid={`collection-thumbs-${t.slug}`}>
                      {thumbs.map((src, i) => (
                        <div key={src + i} className="aspect-square bg-[#FAFAFA] hairline overflow-hidden">
                          <img
                            src={src}
                            alt={`${t.name} product ${i + 1}`}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-auto pt-6 label-eyebrow flex items-center gap-2 moto-accent group-hover:text-white transition-colors">
                    Enter <ArrowRight size={12} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="border-b border-black/10 py-24 md:py-32" data-testid="home-featured">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-16">
            <div>
              <div className="label-eyebrow moto-accent">Now Available</div>
              <h2 className="font-display text-4xl md:text-6xl mt-3 tracking-tighter">Featured pieces.</h2>
            </div>
            <Link to="/shop" className="label-eyebrow flex items-center gap-2 border-b border-[#FF3B30] moto-accent pb-1">Shop all <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {products.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        </div>
      </section>

      {featuredStories.length > 0 && (
        <section className="border-b border-black/10 py-24 md:py-32 bg-white" data-testid="home-stories">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <div className="label-eyebrow moto-accent">Studio wall</div>
                <h2 className="font-display text-4xl md:text-6xl mt-3 tracking-tighter">Customer stories.</h2>
                <p className="text-sm text-black/55 mt-3 max-w-md">Real photos sent in by real customers. Refreshed at random every page load — see the full wall on the share page.</p>
              </div>
              <Link to="/share-your-story" className="label-eyebrow flex items-center gap-2 border-b border-black pb-1" data-testid="home-stories-cta">
                See the wall <ArrowRight size={14} aria-hidden="true" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-stories-grid">
              {featuredStories.map((s) => (
                <article key={s.id} className="hairline-strong bg-white overflow-hidden" data-testid={`home-story-${s.id}`}>
                  {s.images?.length > 0 && (
                    <Link to="/share-your-story" className="block aspect-square bg-[#FAFAFA] overflow-hidden">
                      <img
                        src={s.images[0]}
                        alt={`${s.name} — ${s.title}`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    </Link>
                  )}
                  <div className="p-5">
                    <div className="label-eyebrow text-black/45 text-[10px]">{s.name}</div>
                    <h3 className="font-display text-xl mt-1 line-clamp-2">{s.title}</h3>
                    <p className="text-sm text-black/65 mt-2 line-clamp-3 leading-relaxed">{s.body}</p>
                    <div className="mt-4 flex items-center gap-3 text-[11px] text-black/55" data-testid={`home-story-stats-${s.id}`}>
                      <span>{(s.reactions?.heart || 0) + (s.reactions?.thumbs_up || 0) + (s.reactions?.laugh || 0) + (s.reactions?.like || 0)} reactions</span>
                      <span>·</span>
                      <span>{s.comments_count || 0} comments</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link to="/share-your-story" className="inline-block bg-black text-white px-8 py-3.5 label-eyebrow hover:bg-[#FF3B30] transition-colors" data-testid="home-stories-share-cta">
                Share your story →
              </Link>
            </div>
          </div>
        </section>
      )}

      {topReviews.length > 0 && (
        <section className="border-b border-black/10 py-24 md:py-32 bg-[#FAFAFA]" data-testid="home-reviews">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <div className="label-eyebrow moto-accent">{reviewsSec.eyebrow}</div>
                <h2 className="font-display text-4xl md:text-6xl mt-3 tracking-tighter">{reviewsSec.title}</h2>
              </div>
              <Link to={reviewsSec.cta_href || "/reviews"} className="label-eyebrow flex items-center gap-2 border-b border-black pb-1" data-testid="home-reviews-read-all">{reviewsSec.subtitle || "Read all reviews"} <ArrowRight size={14} /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10" data-testid="home-reviews-grid">
              {topReviews.map((r) => (
                <article key={r.id} className="bg-white p-8 md:p-10 flex flex-col" data-testid={`home-review-${r.id}`}>
                  <div className="text-[#FF3B30] text-2xl tracking-tight" aria-label={`${r.rating} stars`}>
                    {"★".repeat(r.rating)}<span className="text-black/15">{"★".repeat(5 - r.rating)}</span>
                  </div>
                  {r.title && <div className="font-display text-2xl tracking-tight mt-4">{r.title}</div>}
                  <p className="mt-4 text-sm text-black/70 leading-relaxed flex-1 line-clamp-6 whitespace-pre-wrap">{r.body}</p>
                  {r.product_id && r.product_title && (
                    <Link to={`/product/${r.product_id}`} className="mt-6 flex items-center gap-3 hairline p-2 group" data-testid={`home-review-prod-${r.id}`}>
                      {r.product_image && (
                        <img src={resolveImageUrl(r.product_image)} alt="" className="w-12 h-12 object-cover" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] label-eyebrow text-black/40">Reviewing</div>
                        <div className="text-sm font-medium line-clamp-1 group-hover:moto-accent transition-colors">{r.product_title}</div>
                      </div>
                      <ArrowRight size={14} className="text-black/40 group-hover:text-[#FF3B30] transition-colors" />
                    </Link>
                  )}
                  <div className="mt-6 flex items-center justify-between">
                    <div className="label-eyebrow">{r.name}</div>
                    {r.verified_purchase && (
                      <span className="label-eyebrow text-[10px] bg-black text-white px-2 py-0.5">Verified</span>
                    )}
                  </div>
                </article>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link to={reviewsSec.cta_href || "/reviews"} className="inline-block bg-black text-white px-8 py-3.5 label-eyebrow hover:bg-[#FF3B30] transition-colors" data-testid="home-reviews-cta">
                {reviewsSec.cta_label || "Read all reviews →"}
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
